import type { Task } from '@/types/task'
import type { CalendarEvent } from '@/hooks/useGoogleCalendar'
import type { Routine, ActionableInstance } from '@/types/actionable'
import type { TimelineItem } from '@/types/timeline'
import type { DaySection } from '@/lib/timeUtils'
import type { Contact } from '@/types/contact'
import type { AttentionItem } from './attention'
import type { Layer } from '@/lib/domains'
import type { DayPlan } from './dayPlan'

// A single selected id, an array of selected ids (multi-select / union), or
// null/undefined/[] meaning "everyone". The pseudo-id 'unassigned' is allowed.
export type AssigneeFilter = string | readonly string[] | null | undefined

export interface TodayDataInput {
  tasks: Task[]
  events: CalendarEvent[]
  routines: Routine[]
  dateInstances: ActionableInstance[]
  viewedDate: Date
  selectedAssignee: AssigneeFilter
  hideRoutines: boolean
  /** The checked layers. Unsorted is a layer, not a wildcard. */
  layers: ReadonlySet<Layer>
  eventNotesMap?: Map<string, { notes?: string; assignedTo?: string | null; isFree?: boolean }>
  eventContextOverrides?: Map<string, 'work' | 'family' | 'personal'>
  getDomainForCalendar?: (calendarId?: string, calendarName?: string) => 'work' | 'family' | 'personal' | null
  /** Present for parity with legacy deps; not used by current logic. */
  projectsMap?: Map<string, { name: string }>
  contactsMap?: Map<string, Contact>
  /**
   * When set, completed tasks whose `updatedAt` is older than this timestamp
   * (ms epoch) are dropped from the *displayed* list — they linger briefly
   * after being checked off, then disappear. Counts are unaffected (they use
   * the full pool). Undefined = never hide completed (current desktop behavior).
   */
  completedLingerCutoff?: number
  /**
   * Start of the current week, per the user's `weekStartsOn`. Required: it is
   * the anchor `selectNeedsAttention` compares a `bucket='week'` task's own
   * `weekStart` against to decide "stranded" vs "still this week". A fallback
   * value here (e.g. substituting "today") would fabricate a wrong anchor and
   * misclassify a task correctly placed on the real current week — silently,
   * since every calendar-date compare "succeeds." Computed at the React
   * boundary (readCadenceConfig touches localStorage — this stays pure).
   */
  weekStart: Date
  /** Whose Today this is: focus (task_focus) is personal. Without it, any
   *  person's choice for the day counts. */
  userId?: string | null
  /** Weekly routines with no day of their own — they join the To plan list
   *  (lib/week/unhomedRoutines), the same set the Planning dock draws. */
  unhomedRoutines?: Routine[]
  /** A no-school day's school routines, shown anyway (the calendar was wrong). */
  showSchoolRoutines?: boolean
}

export interface TodayCounts {
  completedCount: number
  incompleteOverdue: number
  actionableCount: number
  totalItems: number
  progressPercent: number
}

export interface TodayData {
  isToday: boolean
  /** Overdue but inside the grace window — Today's "Carried over" lane. */
  overdueTasks: Task[]
  /** Overdue past the grace window. Never rendered as Today rows: the page
   *  shows a single pointer line and the review surface owns the list. */
  slippedTasks: Task[]
  /** What needs attention, and why — Today's one bounded signal. Replaces the
   *  inbox/week/month pools, which were backlog rendered on an execution
   *  surface. */
  attentionItems: AttentionItem[]
  completedInboxTasks: Task[]
  grouped: Record<DaySection, TimelineItem[]>
  /** What the Today pin holds: dated-but-unchosen tasks, available routine
   *  occurrences, and this week's/month's lists (dayPlan.ts). */
  dayPlan: DayPlan
  sectionsOrder: DaySection[]
  counts: TodayCounts
  /** A no-school day (noSchool.ts): the calendar entry that says so, the
   *  school routines it left off, and whether they're shown anyway. Null on a
   *  school day, or when no routine was affected. */
  schoolOff?: { reason: string; names: string[]; shown: boolean } | null
}

export const SECTIONS_ORDER: DaySection[] = [
  'allday', 'earlyMorning', 'morning', 'afternoon', 'evening', 'night', 'unscheduled',
]

/**
 * A fully-keyed, empty section record. Use this instead of writing an object
 * literal — a hand-written literal is how five-key fixtures survived the
 * earlyMorning/night split and hid a real regression from every test. Callers
 * spread their own sections over the result:
 *
 *   { ...emptySections<TimelineItem>(), morning: [item] }
 */
export function emptySections<T>(): Record<DaySection, T[]> {
  const out = {} as Record<DaySection, T[]>
  for (const s of SECTIONS_ORDER) out[s] = []
  return out
}

export const EMPTY_TODAY_DATA: TodayData = {
  isToday: false,
  overdueTasks: [],
  slippedTasks: [],
  attentionItems: [],
  completedInboxTasks: [],
  grouped: emptySections<TimelineItem>(),
  dayPlan: {
    toPlan: [], unfinished: [], olderUnfinished: 0, chooserTasks: [], chooserRoutines: [],
    carried: [], scheduled: [], available: [], week: [], month: [],
    counts: { scheduled: 0, available: 0 },
    offMainTaskIds: new Set(), offMainRoutineItemIds: new Set(), plannedExtraTasks: [],
  },
  sectionsOrder: SECTIONS_ORDER,
  counts: { completedCount: 0, incompleteOverdue: 0, actionableCount: 0, totalItems: 0, progressPercent: 0 },
}
